import { useState, useCallback } from 'react'
import { getUploadUrl } from '@/api/userApi'
import axios from 'axios'

export interface URLPdf {
  key: string
  url: string
}

export interface UsePdfUploadOptions {
  linkFor: 'eventScheduleDocument' | 'eventInfoDocument'
  maxFileSize?: number
  onSuccess?: (key: string, file: File) => void
  onError?: (error: string) => void
}

export interface UsePdfUploadReturn {
  uploadPdf: (file: File | null) => Promise<string | undefined>
  isUploading: boolean
  uploadProgress: number
  removePdf: () => void
  getExistingPdfFile: (pdf?: URLPdf) => Promise<File | null>
}

export default function usePdfUpload(
  options: UsePdfUploadOptions
): UsePdfUploadReturn {
  const {
    linkFor,
    maxFileSize = 5 * 1024 * 1024, // 5MB
    onSuccess,
    onError,
  } = options

  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  /**
   * Validate PDF file
   */
  const validatePdf = useCallback(
    (file: File): string | null => {
      if (file.type !== 'application/pdf') {
        return 'Only PDF files are allowed'
      }

      if (file.size > maxFileSize) {
        const sizeMB = Math.round(maxFileSize / (1024 * 1024))
        return `PDF size must be less than ${sizeMB}MB`
      }

      return null
    },
    [maxFileSize]
  )

  /**
   * Upload a single PDF
   */
  const uploadPdf = useCallback(
    async (file: File | null) => {
      if (!file) return

      const error = validatePdf(file)
      if (error) {
        onError?.(error)
        return
      }

      setIsUploading(true)
      setUploadProgress(0)

      try {
        const [uploadData] = await getUploadUrl({
          presignPayload: [
            {
              linkFor,
              fileType: file.type,
            },
          ],
        })

        await axios.request({
          method: 'put',
          maxBodyLength: Infinity,
          url: uploadData.url,
          headers: {
            'Content-Type': file.type,
            'x-ms-blob-type': 'BlockBlob',
          },
          data: file,
          onUploadProgress: progressEvent => {
            if (progressEvent.total) {
              const progress =
                (progressEvent.loaded / progressEvent.total) * 100
              setUploadProgress(Math.round(progress))
            }
          },
        })

        setUploadProgress(100)
        onSuccess?.(uploadData.key, file)

        return uploadData.key
      } catch (error) {
        console.error('PDF upload failed:', error)
        onError?.('Failed to upload PDF. Please try again.')
      } finally {
        setIsUploading(false)
      }
    },
    [linkFor, validatePdf, onSuccess, onError]
  )

  /**
   * Convert existing PDF URL to File
   */
  const getExistingPdfFile = useCallback(
    async (pdf?: URLPdf): Promise<File | null> => {
      if (!pdf?.url) return null

      try {
        const response = await axios.get(pdf.url, {
          responseType: 'blob',
        })

        const blob = response.data

        return new File([blob], 'Existing PDF.pdf', {
          type: 'application/pdf',
        })
      } catch (error) {
        console.error('Failed to convert PDF URL to file:', error)
        onError?.('Failed to process existing PDF')
        return null
      }
    },
    [onError]
  )

  /**
   * Reset PDF state (UI-level removal)
   */
  const removePdf = () => {
    setUploadProgress(0)
  }

  return {
    uploadPdf,
    isUploading,
    uploadProgress,
    removePdf,
    getExistingPdfFile,
  }
}
